import { ThemeEvents } from '@theme/events';

/**
 * GA4 dataLayer events for the Volero GTM container (GTM-N6XGCTL5), unprefixed event names.
 *
 * Owns add_to_cart, remove_from_cart, view_cart, add_to_wishlist, remove_from_wishlist, view_item.
 * Tweakwise owns the list/search events, the TAGGRS pixel owns checkout.
 * Spec: id/item_id = SKU, item_variant = variant id, prices VAT-inclusive to 2 decimals, `va` needs
 * a custom dimension in the container. Module-scope singleton by design — state must survive
 * section re-renders, do not convert to a Component.
 */

/** Cart lines keyed by line key, captured after each settled cart state, to diff the next change against. */
let cartSnapshot = null;

/** Tail of the reconcile queue; chaining stops two updates diffing the same snapshot. */
let reconcileQueue = Promise.resolve();

/** Guards against double-pushing view_cart when several components announce the same cart load. */
let viewCartPushed = false;

/** Declared before init() calls observeCartDrawer — a `let` after that call is still in its TDZ. */
let observedDialog = null;
let dialogObserver = null;

/** Item data captured when a removal starts, keyed by product id. */
const removedItemSnapshots = new Map();

/**
 * Push onto dataLayer, resetting `ecommerce` first so GA4 can't merge items from a prior event.
 * @param {Record<string, any>} payload
 */
function push(payload) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push(payload);
}

/**
 * Shopify money is integer cents; the spec wants decimals to 2 places.
 * @param {number} cents
 * @returns {number}
 */
function toPrice(cents) {
  return Math.round(Number(cents) || 0) / 100;
}

/**
 * Build a GA4 item. Every event goes through here so all payloads carry an identical field set.
 * @param {object} source
 * @param {string} source.sku
 * @param {string} [source.name]
 * @param {number} source.price - Already in currency units, not cents
 * @param {number} [source.quantity]
 * @param {string} [source.brand]
 * @param {string} [source.category]
 * @param {string} [source.variantId]
 * @param {string} [source.variantLabel]
 * @param {number} index
 * @param {string} [currency]
 * @returns {Record<string, any>}
 */
function toItem(source, index, currency) {
  return {
    id: source.sku,
    item_id: source.sku,
    item_name: source.name || '',
    currency,
    quantity: source.quantity ?? 1,
    index,
    price: source.price,
    item_brand: source.brand || '',
    item_category: source.category || '',
    item_variant: source.variantId || '',
    va: source.variantLabel || '',
  };
}

/**
 * Map a Shopify cart line onto the shared item shape. Brand comes from the `_apc_brand` property;
 * `vendor` is the supplier, so it is only a fall back for lines added before that shipped.
 * @param {any} line - A line item from /cart.js
 * @param {number} index
 * @param {number} [quantity] - Overrides the line quantity, for partial add/remove diffs
 * @param {string} [currency]
 * @returns {Record<string, any>}
 */
function lineToItem(line, index, quantity, currency) {
  return toItem(
    {
      sku: line.sku || String(line.product_id || ''),
      name: line.product_title || line.title || '',
      price: toPrice(line.price),
      quantity: quantity ?? line.quantity,
      brand: line.properties?._apc_brand || line.vendor,
      category: line.product_type,
      variantId: String(line.variant_id || ''),
      variantLabel: line.variant_title,
    },
    index,
    currency,
  );
}

/**
 * Total value of a set of items, VAT inclusive: price x quantity.
 * @param {Array<Record<string, any>>} items
 * @returns {number}
 */
function totalValue(items) {
  const sum = items.reduce((acc, item) => acc + item.price * item.quantity, 0);
  return Math.round(sum * 100) / 100;
}

/**
 * Index a cart's lines by line key so two snapshots can be compared.
 * @param {any} cart
 * @returns {Map<string, any>}
 */
function indexLines(cart) {
  const map = new Map();
  for (const line of cart?.items ?? []) {
    map.set(line.key, line);
  }
  return map;
}

/**
 * Fetch the authoritative cart — the theme's cart events carry no price or SKU data.
 * @returns {Promise<any|null>}
 */
async function fetchCart() {
  try {
    const response = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

/**
 * Diff two cart states. A quantity change surfaces as a partial add or remove of the delta.
 * @param {any} before
 * @param {any} after
 * @returns {{ added: Array<any>, removed: Array<any> }}
 */
function diffCarts(before, after) {
  const beforeLines = indexLines(before);
  const afterLines = indexLines(after);
  const added = [];
  const removed = [];

  for (const [key, line] of afterLines) {
    const previous = beforeLines.get(key);
    const delta = line.quantity - (previous?.quantity ?? 0);
    if (delta > 0) added.push({ line, quantity: delta });
  }

  for (const [key, line] of beforeLines) {
    const current = afterLines.get(key);
    const delta = (current?.quantity ?? 0) - line.quantity;
    if (delta < 0) removed.push({ line, quantity: -delta });
  }

  return { added, removed };
}

/**
 * Queue a reconcile behind any in flight; the snapshot is read before an await.
 * @returns {Promise<void>}
 */
function queueReconcileCart() {
  reconcileQueue = reconcileQueue.then(reconcileCart, reconcileCart);
  return reconcileQueue;
}

/**
 * Compare the cart against the last snapshot and push the events the change implies. A variant
 * swap yields both an add and a remove; `is_bundle` is a multi-line add, not the event's count.
 */
async function reconcileCart() {
  const before = cartSnapshot;
  const after = await fetchCart();
  if (!after) return;

  // First settled cart of the session: record the baseline, don't report it as activity.
  if (!before) {
    cartSnapshot = after;
    return;
  }

  cartSnapshot = after;

  const { added, removed } = diffCarts(before, after);
  const currency = after.currency;

  if (added.length) {
    const items = added.map((entry, index) => lineToItem(entry.line, index, entry.quantity, currency));
    push({
      event: 'add_to_cart',
      is_bundle: items.length > 1,
      ecommerce: { currency, value: totalValue(items), items },
    });
  }

  if (removed.length) {
    const items = removed.map((entry, index) => lineToItem(entry.line, index, entry.quantity, currency));
    push({
      event: 'remove_from_cart',
      ecommerce: { currency, value: totalValue(items), items },
    });
  }
}

/**
 * Read the Liquid-rendered context blob for the current page.
 * @returns {any|null}
 */
function readContext() {
  const node = document.querySelector('[data-apc-datalayer-context]');
  if (!node?.textContent) return null;
  try {
    return JSON.parse(node.textContent);
  } catch {
    return null;
  }
}

/** view_item — PDP load. Rendered server-side, so no request is needed. */
function pushViewItem(context) {
  const product = context?.product;
  if (!product) return;

  const items = [
    toItem(
      {
        sku: product.sku,
        name: product.title,
        price: product.price,
        brand: product.brand,
        category: product.category,
        variantId: String(product.variant_id || ''),
        variantLabel: product.variant_title,
      },
      0,
      context.currency,
    ),
  ];

  push({
    event: 'view_item',
    ecommerce: { currency: context.currency, value: product.price, items },
  });
}

/** view_cart — cart page or drawer open. Queued with the reconciles so it can't clobber a diff. */
function pushViewCart() {
  reconcileQueue = reconcileQueue.then(runViewCart, runViewCart);
  return reconcileQueue;
}

async function runViewCart() {
  if (viewCartPushed) return;

  const cart = await fetchCart();
  if (!cart) return;

  cartSnapshot = cart;
  if (!cart.items?.length) return;

  viewCartPushed = true;
  const items = cart.items.map((line, index) => lineToItem(line, index, undefined, cart.currency));

  push({
    event: 'view_cart',
    ecommerce: { currency: cart.currency, value: totalValue(items), items },
  });
}

/**
 * Wishlist add/remove; only the event name differs, per `inWishlist`.
 * @param {any} detail
 */
function pushWishlist(detail) {
  const productId = detail?.productId ? String(detail.productId) : '';

  // clearItems() dispatches count-only on logout sync, not a user action.
  if (!productId || typeof detail.inWishlist !== 'boolean') return;

  // Item data comes off the button, or its snapshot if the card is already gone.
  const button = document.querySelector(`wishlist-button[data-product-id="${CSS.escape(productId)}"]`);
  const data = button?.dataset?.apcSku ? button.dataset : removedItemSnapshots.get(productId);
  removedItemSnapshots.delete(productId);

  // A hollow item is worse for GA4 than no event.
  if (!data?.apcSku) return;

  const price = Number(data.apcPrice) || 0;
  const currency = readContext()?.currency || 'EUR';

  // Klaviyo's own onsite script (loaded via its app embed) isn't part of the native
  // Shopify integration for wishlist activity, so this is pushed separately from GA4.
  if (window.klaviyo) {
    window.klaviyo.track(detail.inWishlist ? 'Added To Wishlist' : 'Removed From Wishlist', {
      ProductName: data.apcName,
      SKU: data.apcSku,
      Brand: data.apcBrand,
      Category: data.apcCollectionLine || '',
      VariantID: data.apcVariantId,
      Price: price,
      Currency: currency,
      URL: window.location.href,
    });
    console.log('wishlist event pushed to klaviyo');
  }
}

// Adds, quantity updates and removals all arrive here; the cart is re-read and diffed either way.
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const detail = /** @type {any} */ (event).detail;
  if (detail?.data?.didError) return;

  queueReconcileCart();

  // Re-bind if the drawer node was replaced.
  observeCartDrawer();
});

// Fires while the card is still in the DOM, unlike the count event below.
document.addEventListener('apa:wishlist:removed', (event) => {
  const productId = /** @type {any} */ (event).detail?.productId;
  if (!productId) return;

  const button = document.querySelector(`wishlist-button[data-product-id="${CSS.escape(String(productId))}"]`);
  if (button?.dataset?.apcSku) removedItemSnapshots.set(String(productId), { ...button.dataset });
});

document.addEventListener('apa:wishlist:count', (event) => {
  pushWishlist(/** @type {any} */ (event).detail ?? {});
});

// Re-push view_item on variant change; the id guard stops the initial render double-firing.
let lastViewItemVariantId = null;

document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  const html = /** @type {any} */ (event).detail?.data?.html;
  const node = html?.querySelector?.('[data-apc-datalayer-context]');
  if (!node) return;

  /** @type {any} */
  let context = null;
  try {
    context = JSON.parse(node.textContent);
  } catch {
    return;
  }

  if (context?.template !== 'product') return;

  const variantId = String(context.product?.variant_id || '');
  if (!variantId || variantId === lastViewItemVariantId) return;

  lastViewItemVariantId = variantId;
  pushViewItem(context);
});

// Page-load events.
function init() {
  const context = readContext();

  if (context?.template === 'product') {
    // Seed the guard with what load reported, so returning to this variant does not re-fire.
    lastViewItemVariantId = String(context.product?.variant_id || '');
    pushViewItem(context);
  }
  if (context?.template === 'cart') pushViewCart();

  // Baseline the cart so the first add diffs against a real state.
  if (context?.template !== 'cart') queueReconcileCart();

  observeCartDrawer();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

/** The drawer counts as a cart view; observing its `open` attribute is the only signal it emits. */
function observeCartDrawer() {
  const dialog = document.querySelector('cart-drawer-component dialog');
  if (!dialog || dialog === observedDialog) return;

  // Drop the observer on the previous node.
  dialogObserver?.disconnect();

  observedDialog = dialog;
  dialogObserver = new MutationObserver(() => {
    if (dialog.open) {
      pushViewCart();
    } else {
      viewCartPushed = false;
    }
  });

  dialogObserver.observe(dialog, { attributes: true, attributeFilter: ['open'] });
}
